import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, pgTable, text, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsHighPrecision } from '../../custom-type'
import { organization } from '../auth'
import { RoyaltyTransaction } from './royalty-transaction'

export const RunningBalance = pgTable(
  'running_balance',
  {
    id: uuid().primaryKey().defaultRandom(),
    organizationId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'restrict' }),
    balance: numericAsHighPrecision().notNull().default(0),
    currency: text().notNull().default('IDR'),
    lastTransactionId: uuid().references(() => RoyaltyTransaction.id, {
      onDelete: 'restrict',
    }),
    lastUpdatedAt: timestamp({ mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    uniqueIndex('running_balance_organization_uidx').on(t.organizationId),
    index('running_balance_last_updated_at_idx').on(t.lastUpdatedAt),
  ],
)

export const runningBalanceRelations = relations(RunningBalance, ({ one }) => ({
  organization: one(organization, {
    fields: [RunningBalance.organizationId],
    references: [organization.id],
  }),
  lastTransaction: one(RoyaltyTransaction, {
    fields: [RunningBalance.lastTransactionId],
    references: [RoyaltyTransaction.id],
  }),
}))

export const InsertRunningBalanceSchema = createInsertSchema(RunningBalance).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateRunningBalanceSchema = createUpdateSchema(RunningBalance).partial().omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
})

export const SelectRunningBalanceSchema = createSelectSchema(RunningBalance)

export type InsertRunningBalance = typeof RunningBalance.$inferInsert
export type UpdateRunningBalance = z.infer<typeof UpdateRunningBalanceSchema>
export type SelectRunningBalance = typeof RunningBalance.$inferSelect
